import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { PasswordInputChecklist } from "@/components/passwordForm/PasswordInputChecklist";
import { Button } from "@/components/ui/button";
import FormField from "@/components/ui/formField";

import { twMerge } from "tailwind-merge";

import { PASSWORD_FORM_RULES } from "./data";
import { ChangePasswordFormData, ChangePasswordFormProps } from "./types";

export const ChangePasswordForm = ({
  isSubmitting,
  onSubmit,
  onClose
}: ChangePasswordFormProps) => {
  const formProviderProps = useForm<ChangePasswordFormData>({
    mode: "onChange",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: ""
    }
  });
  const { register, formState, watch, trigger, handleSubmit, getValues } =
    formProviderProps;
  const disableButton = useMemo(
    () => isSubmitting || !formState.isValid,
    [isSubmitting, formState.isValid]
  );

  return (
    <FormProvider {...formProviderProps}>
      <form onSubmit={handleSubmit(() => onSubmit?.({ ...getValues() }))}>
        <div className="flex flex-col gap-6" data-testid="change-password-form">
          <FormField
            data-testid="change-password--current-password-input"
            register={register}
            placeHolder="Current password"
            type="password"
            name="currentPassword"
            rules={PASSWORD_FORM_RULES.currentPassword}
            autoCleanError={false}
            hasSpacingOnFocus={false}
            disabled={isSubmitting}
          />
          <PasswordInputChecklist inputValue={watch("newPassword")} />
          <div className="flex flex-col gap-4">
            {/* The error of password input will be shown on checklist above. There will be no red error on input */}
            <FormField
              data-testid="change-password--new-password-input"
              register={register}
              placeHolder="New password"
              type="password"
              name="newPassword"
              rules={PASSWORD_FORM_RULES.newPassword}
              onChange={() => {
                // need to check the matchness with confirm password too when confirm password is filled
                if (watch("confirmNewPassword")) {
                  trigger("confirmNewPassword");
                }
              }}
              autoCleanError={false}
              hasSpacingOnFocus={false}
              disabled={isSubmitting}
            />
            <FormField
              data-testid="change-password--confirm-new-password-input"
              register={register}
              placeHolder="Confirm password"
              type="password"
              name="confirmNewPassword"
              rules={PASSWORD_FORM_RULES.confirmNewPassword}
              error={formState.errors.confirmNewPassword?.message}
              autoCleanError={false}
              hasSpacingOnFocus={false}
              disabled={isSubmitting}
            />
          </div>
          <div className="flex gap-3 justify-between">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl font-semibold text-base py-3 mt-8 w-full"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={disableButton}
              className={twMerge(
                "rounded-xl font-semibold text-base py-3 mt-8 w-full",
                disableButton && "bg-gray-300 disabled:opacity-70"
              )}
            >
              Update
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
